/**
 * `GET`/`PATCH /v1/me/profile` — ett menneskes egen rad (QA12 #7).
 *
 * EGEN NØKKEL OG IKKE UNDER `me`, selv om begge handler om kalleren. `qk.me()` er appskallets
 * svar og hentes på hver sidelast; profilen er tre tabeller og hentes av to flater. Å legge dem
 * under samme prefiks ville gjort at en lagret telefon ugyldiggjorde hele skallet — og skallet
 * ville hentet seg selv på nytt for en opplysning det ikke viser.
 */
import { queryOptions, useMutation, type UseMutationResult } from '@tanstack/react-query';

import { myProfileDtoSchema, type MyProfileDto, type UpdateMyProfileRequest } from '@tvitvi/shared';

import { apiRequest } from '@/api/client';
import { qk } from '@/api/query-keys';
import { CACHE } from '@/api/cache-tiers';

const PATH = '/me/profile';

export function myProfileQueryOptions() {
  return queryOptions({
    queryKey: qk.myProfile(),
    queryFn: ({ signal }): Promise<MyProfileDto> =>
      apiRequest({ path: PATH, schema: myProfileDtoSchema, signal }),
    ...CACHE.detail,
  });
}

export function useUpdateMyProfile(): UseMutationResult<
  MyProfileDto,
  unknown,
  UpdateMyProfileRequest
> {
  return useMutation({
    mutationFn: (body: UpdateMyProfileRequest): Promise<MyProfileDto> =>
      apiRequest({ path: PATH, method: 'PATCH', body, schema: myProfileDtoSchema }),
    /*
     * `qk.me()` ER med, og det er den ene gangen det er riktig: skriver man navnet sitt, står det
     * gamle i toppmenyen til noe annet tilfeldigvis henter skallet.
     *
     * Kontaktlista og medlemslista viser den samme raden og burde også vært med, men de ligger
     * under `qk.org(orgId)` og denne kroken kjenner ikke org-en. Kalleren kan sende dem inn den
     * dagen det trengs; i dag er begge listene bak en navigering, og en navigering henter dem.
     */
    meta: { invalidates: [qk.myProfile(), qk.me()] },
  });
}
